import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errDetail, fmtIST } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { EmptyState, ErrorState, ListSkeleton, TestBadge } from "@/components/common";
import { MergePreviewCard } from "@/components/CustomerMerge";
import { IncludeTestToggle } from "@/components/IncludeTestToggle";
import { useIncludeTest, withIncludeTest } from "@/hooks/useIncludeTest";
import { formatCount, truncationNote } from "@/lib/counts";
import { ShieldCheck, UserPlus, CalendarClock, Briefcase, CheckCircle2, XCircle, AlertTriangle, ChevronDown, ChevronUp, History, ClipboardList, Clock, SkipForward } from "lucide-react";

const TYPE_LABEL = { ownership_change: "Ownership change", product_addition: "Product addition", signup_case: "Signup → Case activation", exception: "Exception", customer_merge: "Customer merge", product_change: "Change product", discontinue_and_replace: "Discontinue & add new product", payment_reallocation: "Payment reallocation", product_activation: "Product activation", task_completion: "Task completion",
  // 2026-09 P2 round 3 — Student Visa's OWN product-specific signup
  // decisions, deliberately distinct types from signup_case. Round 4:
  // Partnership's own label dropped "Commercial Signup" — the stage of
  // that name was removed as a duplicate substitute for "Signup".
  // 2026-09 R12 correction round 2 (owner safety fix) — Partnership has no
  // true commercial Signup at this stage; its label must never claim the
  // customer paid or signed up. Renamed to match the gate this decision
  // actually is: approving the Case's continued progress toward Visa, once
  // admission succeeded and the Visa/service fee cleared. SA_PREMIUM's own
  // label is untouched — its Signup is genuine and immediate.
  sa_premium_signup: "Student Visa Premium — Signup",
  sa_partnership_signup: "Student Visa Partnership — Approve Process Handover" };
// product_activation's own `kind` (2026-09 P2) — never a Leadership decision
// (status is never "pending"), so this is display-only labeling for the
// History tab. Distinguishes a genuine outstanding Commercial Plan payment
// from an automatic activation from an unexpected error — never the
// generic Skill Migration "Signup payment outstanding" wording.
const PRODUCT_ACTIVATION_KIND_LABEL = {
  financial_prerequisite: "Financial prerequisite",
  auto_activation: "Activated automatically",
  activation_failure: "Activation failed",
};
const PRODUCT_ACTIVATION_BADGE_CLASS = {
  blocked: "bg-amber-500/15 text-amber-600",
  resolved: "bg-emerald-500/15 text-emerald-600",
  completed: "bg-emerald-500/15 text-emerald-600",
  failed: "bg-red-500/15 text-red-600",
};
const inr = (v) => (v == null ? "—" : `₹${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`);

// Recovery-state -> badge tone for Past unassigned consultations. Purely a
// display choice — the state itself always comes from the backend's own
// canonical classification (core.is_task_overdue, cancelled_no_consultant,
// the rebooking task, a later appointment on the same opportunity), never
// invented client-side.
const PAST_CONSULT_BADGE = {
  unresolved: "bg-red-500/15 text-red-600 dark:text-red-400",
  auto_cancelled_no_task: "bg-red-500/15 text-red-600 dark:text-red-400",
  followup_overdue: "bg-red-500/15 text-red-600 dark:text-red-400",
  followup_open: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  followup_completed: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  rebooked: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
};

// Cancellation actor type -> label/badge tone. The actor type itself is
// always the backend's own cancelled_by_type (services.cancel_appointment)
// — a customer cancellation can never render as "Consultant" here, since
// the value is read directly from that field, never inferred client-side.
const CANCEL_ACTOR_LABEL = { customer: "Customer", employee: "Consultant", admin: "Leadership / Admin", system: "System" };
const CANCEL_ACTOR_BADGE = {
  customer: "bg-sky-500/15 text-sky-600 dark:text-sky-400",
  employee: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  admin: "bg-purple-500/15 text-purple-600 dark:text-purple-400",
  system: "bg-slate-500/15 text-slate-600 dark:text-slate-400",
};

/* Session-remembered collapse state (Section 1 — "remember during the
   current session if easy") — sessionStorage rather than component state
   so it survives a page reload within the same browser tab/session,
   cleared automatically when the tab closes. Never causes a backend call
   or mutates any assignment — purely a display toggle. */
const COLLAPSE_KEY = "salesos.approvalsUnassigned.collapsed";
const loadCollapseState = () => {
  try { return JSON.parse(sessionStorage.getItem(COLLAPSE_KEY) || "{}"); } catch { return {}; }
};
const saveCollapseState = (state) => {
  try { sessionStorage.setItem(COLLAPSE_KEY, JSON.stringify(state)); } catch { /* ignore */ }
};

const SectionHeader = ({ icon: Icon, title, count, note, sortOptions, sort, onSortChange, collapsed, onToggle, testId }) => (
  <div className="flex items-center gap-2 flex-wrap">
    <h3 className="text-sm font-medium flex items-center gap-1.5"><Icon className="h-4 w-4" />{title} (<span data-testid={`${testId}-count`}>{count}</span>)</h3>
    {note && <span className="text-[11px] text-muted-foreground" data-testid={`${testId}-note`}>{note}</span>}
    {sortOptions && (
      <Select value={sort} onValueChange={onSortChange}>
        <SelectTrigger data-testid={`${testId}-sort`} className="h-7 text-xs w-40"><SelectValue /></SelectTrigger>
        <SelectContent>{sortOptions.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
      </Select>
    )}
    <Button size="sm" variant="ghost" className="h-7 text-xs ml-auto" data-testid={`${testId}-toggle`} onClick={onToggle}>
      {collapsed ? <>Expand <ChevronDown className="h-3.5 w-3.5 ml-1" /></> : <>Collapse <ChevronUp className="h-3.5 w-3.5 ml-1" /></>}
    </Button>
  </div>
);

/* Reused for Lead and Process Case inline assignment — both call an
   atomic assign endpoint (compare-and-swap on the current owner) and
   surface a 409 as a clear conflict, never a silent overwrite. */
const AssignDialog = ({ open, setOpen, title, employees, onConfirm, busy, testIdPrefix }) => {
  const [target, setTarget] = useState("");
  useEffect(() => { if (open) setTarget(""); }, [open]);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-sm" data-testid={`${testIdPrefix}-dialog`}>
        <DialogHeader><DialogTitle className="font-heading">{title}</DialogTitle></DialogHeader>
        <div className="space-y-1">
          <Label className="text-xs">Assign to</Label>
          <Select value={target} onValueChange={setTarget}>
            <SelectTrigger data-testid={`${testIdPrefix}-target`}><SelectValue placeholder="Pick employee" /></SelectTrigger>
            <SelectContent>{employees.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.role})</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button data-testid={`${testIdPrefix}-confirm`} disabled={!target || busy} onClick={() => onConfirm(target)}>
            {busy ? "Assigning…" : "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* 2026-09 — Leadership hands an ALREADY-assigned overdue task to a
   different employee. Deliberately distinct from TaskAssignDialog below
   (that one only ever claims a task with no current owner). Task assignee
   and opportunity owner are separate concepts — the checkbox is OFF by
   default and hidden entirely when the task has no linked opportunity. */
const ReassignTaskDialog = ({ open, setOpen, task, employees, onConfirm, busy }) => {
  const [target, setTarget] = useState("");
  const [alsoChangeOwner, setAlsoChangeOwner] = useState(false);
  const [reason, setReason] = useState("");
  useEffect(() => { if (open) { setTarget(""); setAlsoChangeOwner(false); setReason(""); } }, [open]);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-sm" data-testid="overdue-task-reassign-dialog">
        <DialogHeader><DialogTitle className="font-heading">Reassign task</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {task && (
            <div className="text-xs text-muted-foreground">
              <div className="font-medium text-foreground">{task.title}</div>
              {task.contact_name} {task.product_name ? `· ${task.product_name}` : ""}
              <div className="mt-0.5">Currently: {task.owner_name || task.owner_user_id}</div>
            </div>
          )}
          <div className="space-y-1">
            <Label className="text-xs">New assignee</Label>
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger data-testid="overdue-task-reassign-target"><SelectValue placeholder="Pick employee" /></SelectTrigger>
              <SelectContent>{employees.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.role})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {task?.opportunity_id && (
            <label className="flex items-start gap-2 text-xs">
              <Checkbox data-testid="overdue-task-reassign-also-owner"
                checked={alsoChangeOwner} onCheckedChange={(v) => setAlsoChangeOwner(!!v)} />
              <span>Also change opportunity/customer owner to this employee</span>
            </label>
          )}
          <div className="space-y-1">
            <Label className="text-xs">Reason (required — audited)</Label>
            <Textarea data-testid="overdue-task-reassign-reason" value={reason}
              onChange={(e) => setReason(e.target.value)} placeholder="Why is this task being reassigned?" />
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="overdue-task-reassign-confirm" disabled={!target || !reason.trim() || busy}
            onClick={() => onConfirm(target, alsoChangeOwner, reason.trim())}>
            {busy ? "Reassigning…" : "Reassign"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* Section F — assign one unassigned human-follow-up task directly to an
   employee, with an OPTIONAL deadline. Blank deadline => backend uses its
   existing default task-SLA logic (never a second policy invented here). */
const TaskAssignDialog = ({ open, setOpen, task, employees, onConfirm, busy }) => {
  const [target, setTarget] = useState("");
  const [due, setDue] = useState("");
  const [reason, setReason] = useState("");
  useEffect(() => { if (open) { setTarget(""); setDue(""); setReason(""); } }, [open]);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-sm" data-testid="unassigned-task-assign-dialog">
        <DialogHeader><DialogTitle className="font-heading">Assign task</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {task && (
            <div className="text-xs text-muted-foreground">
              <div className="font-medium text-foreground">{task.reason}</div>
              {task.contact_name} {task.product_name ? `· ${task.product_name}` : ""}
            </div>
          )}
          <div className="space-y-1">
            <Label className="text-xs">Assign to</Label>
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger data-testid="unassigned-task-assign-target"><SelectValue placeholder="Pick employee" /></SelectTrigger>
              <SelectContent>{employees.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.role})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Deadline (optional)</Label>
            <Input type="datetime-local" data-testid="unassigned-task-assign-due"
              value={due} onChange={(e) => setDue(e.target.value)} />
            <p className="text-[11px] text-muted-foreground">Leave blank to use the standard task deadline.</p>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Reason (required — audited)</Label>
            <Textarea data-testid="unassigned-task-assign-reason" value={reason}
              onChange={(e) => setReason(e.target.value)} placeholder="Why is this task being assigned to them?" />
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="unassigned-task-assign-confirm" disabled={!target || !reason.trim() || busy}
            onClick={() => onConfirm(target, due ? new Date(due).toISOString() : null, reason.trim())}>
            {busy ? "Assigning…" : "Assign"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// 2026-09 Signup RBAC UI correction — mirrors the backend's own
// approvals.type_policies default (routers_extras.py's
// DEFAULT_APPROVAL_TYPE_POLICIES; admin-configurable, but this is the
// shipped default and the only policy configured today) purely so the
// Approve/Reject buttons don't invite a click that the backend will
// always 403 anyway. The backend remains the sole source of truth and
// enforcement either way — if an admin ever reconfigures this policy,
// only this cosmetic gate could go stale, never the real decision.
const APPROVAL_TYPE_ROLE_POLICY = { signup_case: ["manager", "supervisor"] };
// Approval types whose APPROVAL creates a Process Case and so accept
// `assignee_user_id` (the new Case's Process owner) — mirrors the backend's
// ASSIGNEE_APPROVAL_TYPES. It is not sent for any other type or on a
// rejection (the server refuses it there), and it never controls who may decide.
const ASSIGNEE_APPROVAL_TYPES = ["signup_case", "sa_premium_signup"];

export default function ApprovalsPage() {
  const { hasCap, user } = useAuth();
  const [wq, setWq] = useState(null);
  // R7: a failed work-queue load must not masquerade as an empty queue.
  const [wqError, setWqError] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyMeta, setHistoryMeta] = useState({ total: NaN, truncated: false });
  const [decide, setDecide] = useState(null); // {approval, approve}
  const [note, setNote] = useState("");
  const [assignee, setAssignee] = useState("");
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [productOverride, setProductOverride] = useState("");
  const [counsellors, setCounsellors] = useState([]);
  const [salesEmployees, setSalesEmployees] = useState([]);
  const [processEmployees, setProcessEmployees] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState(null);
  // 2026-09 R6 — true while the LIVE payment figures for a Signup-type
  // approval are being (re)fetched from GET /approvals/{id}.
  const [paymentStatusLoading, setPaymentStatusLoading] = useState(false);
  const [acknowledgeShortfall, setAcknowledgeShortfall] = useState(false);
  const [acknowledgeFinancialReconciliation, setAcknowledgeFinancialReconciliation] = useState(false);
  const [acknowledgeGstMismatch, setAcknowledgeGstMismatch] = useState(false);
  const [confirmedTaxTreatment, setConfirmedTaxTreatment] = useState("");
  const [emergencyOverride, setEmergencyOverride] = useState(false);
  // Section 1: collapse/expand, remembered for the session, never mutates
  // the backend. Section 2-4: sort — applied server-side (the full
  // unassigned result set is re-sorted at the DB, not just the rendered
  // page) via query params on /approvals/workqueue.
  // Round 7 Workstream F - TEST records hidden unless an authorised admin turns
  // the visible (URL-scoped) toggle on; every list AND counter below takes it.
  const { includeTest, canInclude, setIncludeTest } = useIncludeTest();
  const [collapsed, setCollapsed] = useState(loadCollapseState);
  const toggleCollapsed = (key) => setCollapsed((c) => {
    const next = { ...c, [key]: !c[key] };
    saveCollapseState(next);
    return next;
  });
  const [leadSort, setLeadSort] = useState("newest");
  const [consultationSort, setConsultationSort] = useState("soonest");
  const [processSort, setProcessSort] = useState("waiting_longest");
  const [pastConsultationSort, setPastConsultationSort] = useState("unresolved_first");
  const [leadAssignTarget, setLeadAssignTarget] = useState(null); // opportunity row
  const [processAssignTarget, setProcessAssignTarget] = useState(null); // case row
  const [taskAssignTarget, setTaskAssignTarget] = useState(null); // unassigned task row
  const [assignBusy, setAssignBusy] = useState(false);
  // Consultation Cancellations (Step 1) — date range is a real server-side
  // filter (refetches the workqueue, same as the sort params above);
  // consultant/reason/actor-type filter the already-fetched, date-bounded
  // rows client-side, same simplicity level as every other list tab here.
  const [cancelledFrom, setCancelledFrom] = useState("");
  const [cancelledTo, setCancelledTo] = useState("");
  const [cancelledConsultantFilter, setCancelledConsultantFilter] = useState("all");
  const [cancelledReasonFilter, setCancelledReasonFilter] = useState("all");
  // Default prioritizes employee/consultant cancellations (this tab's
  // primary purpose) — "All" (including customer/system) is one click away.
  const [cancelledActorFilter, setCancelledActorFilter] = useState("employee_admin");
  const [cancelReasons, setCancelReasons] = useState([]);
  // Overdue tasks (Leadership filters/sort) — employee/product/date are
  // real server-side filters (query params on /approvals/workqueue),
  // applied server-side before pagination so a filtered view's total is
  // always exact, never a slice of some other unfiltered cap. Sort always
  // keys off the task's effective due date, never created_at. P5
  // production hotfix (2026-09): real server-side pagination replaced the
  // old silent 500-row cap that made this endpoint's own "All employees"
  // total wrong whenever the real count exceeded 500.
  const [overdueSort, setOverdueSort] = useState("oldest");
  const [overdueEmployeeFilter, setOverdueEmployeeFilter] = useState("all");
  const [overdueProductFilter, setOverdueProductFilter] = useState("all");
  const [overdueDueFrom, setOverdueDueFrom] = useState("");
  const [overdueDueTo, setOverdueDueTo] = useState("");
  // P5 production hotfix (2026-09) — real server-side pagination replacing
  // the old silent 500-row cap; the badge/empty-state now read the exact
  // overdue_tasks_total the backend returns, never overdue_tasks.length
  // (which is only ever this one page's row count).
  const [overduePage, setOverduePage] = useState(1);
  const OVERDUE_PAGE_SIZE = 50;
  const [reassignTarget, setReassignTarget] = useState(null); // overdue task row
  // Skipped tasks (Leadership filters/sort) — same server-side pattern.
  // Reason is genuinely free text (never a coded field anywhere in
  // SalesOS) so it's a substring search, not a dropdown.
  const [skippedSort, setSkippedSort] = useState("newest");
  const [skippedEmployeeFilter, setSkippedEmployeeFilter] = useState("all");
  const [skippedByFilter, setSkippedByFilter] = useState("all");
  const [skippedProductFilter, setSkippedProductFilter] = useState("all");
  const [skippedFrom, setSkippedFrom] = useState("");
  const [skippedTo, setSkippedTo] = useState("");
  const [skippedReasonFilter, setSkippedReasonFilter] = useState("");

  const load = useCallback(() => {
    const cancelledParams = `${cancelledFrom ? `&cancelled_from=${cancelledFrom}` : ""}${cancelledTo ? `&cancelled_to=${cancelledTo}` : ""}`;
    const overdueParams = `&overdue_sort=${overdueSort}`
      + (overdueEmployeeFilter !== "all" ? `&overdue_employee=${overdueEmployeeFilter}` : "")
      + (overdueProductFilter !== "all" ? `&overdue_product=${overdueProductFilter}` : "")
      + (overdueDueFrom ? `&overdue_due_from=${overdueDueFrom}` : "")
      + (overdueDueTo ? `&overdue_due_to=${overdueDueTo}` : "")
      + `&overdue_page=${overduePage}&overdue_page_size=${OVERDUE_PAGE_SIZE}`;
    // skipped_reason is deliberately NOT sent here — it's free text (see
    // ReasonFilter note below) and filtered client-side over this
    // already server-narrowed set, so typing doesn't fire a request per
    // keystroke.
    const skippedParams = `&skipped_sort=${skippedSort}`
      + (skippedEmployeeFilter !== "all" ? `&skipped_employee=${skippedEmployeeFilter}` : "")
      + (skippedByFilter !== "all" ? `&skipped_by=${skippedByFilter}` : "")
      + (skippedProductFilter !== "all" ? `&skipped_product=${skippedProductFilter}` : "")
      + (skippedFrom ? `&skipped_from=${skippedFrom}` : "")
      + (skippedTo ? `&skipped_to=${skippedTo}` : "");
    api.get(withIncludeTest(`/approvals/workqueue?lead_sort=${leadSort}&consultation_sort=${consultationSort}&process_sort=${processSort}&past_consultation_sort=${pastConsultationSort}${cancelledParams}${overdueParams}${skippedParams}`, includeTest))
      .then((r) => { setWq(r.data); setWqError(null); })
      .catch((e) => setWqError(errDetail(e, "The approval centre didn't load")));
    // R15 U4: History follows the same TEST switch as the tabs beside it, and says when the list was cut at the server's limit (the count in the tab title is then "n of total")
    api.get(withIncludeTest("/approvals?status=all", includeTest)).then((r) => {
      setHistory(r.data.filter((a) => a.status !== "pending"));
      setHistoryMeta({ total: Number(r.headers?.["x-total-count"] ?? NaN), truncated: r.headers?.["x-result-truncated"] === "true" });
    }).catch(() => {});
  }, [leadSort, consultationSort, processSort, pastConsultationSort, cancelledFrom, cancelledTo,
      overdueSort, overdueEmployeeFilter, overdueProductFilter, overdueDueFrom, overdueDueTo, overduePage,
      skippedSort, skippedEmployeeFilter, skippedByFilter, skippedProductFilter, skippedFrom, skippedTo,
      includeTest]);

  // Any overdue filter change (not a page change) restarts at page 1 —
  // otherwise a narrower filter could leave the view on a now-nonexistent
  // later page.
  useEffect(() => { setOverduePage(1); },
    [overdueSort, overdueEmployeeFilter, overdueProductFilter, overdueDueFrom, overdueDueTo]);
  useEffect(() => { load(); }, [load]);
  // Round 7 Workstream E - refetch on regaining focus/visibility so the
  // Unassigned counters never show a stale number after an assignment made
  // elsewhere (Customer 360, Leads, another tab).
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("focus", load);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", load);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [load]);
  useEffect(() => {
    api.get("/users").then((r) => {
      const active = (r.data.users || []).filter((u) => u.is_active);
      setUsers(active);
      setCounsellors(active.filter((u) => ["counsellor", "supervisor", "manager", "admin"].includes(u.role)));
      // Contextual eligibility per assignment kind (Section 6) — never the
      // whole undifferentiated user list.
      setSalesEmployees(active.filter((u) => ["counsellor", "supervisor", "manager", "process", "admin"].includes(u.role)));
    }).catch(() => {});
    // Process-owner candidates are decided by the SERVER's one eligibility
    // rule (GET /cases/assignable-owners — active, human, able to open AND
    // work a Case by live capability), never a hardcoded role-name list here.
    // The endpoint is gated on the same reassign capability as the submit, so
    // a caller without it simply gets an empty list.
    api.get("/cases/assignable-owners").then((r) => setProcessEmployees(r.data || []))
      .catch(() => setProcessEmployees([]));
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/appointments/cancel-reasons").then((r) => setCancelReasons(r.data)).catch(() => {});
  }, []);

  const cancelledConsultations = (wq?.cancelled_consultations || []).filter((r) => {
    if (cancelledConsultantFilter !== "all" && r.counsellor_user_id !== cancelledConsultantFilter) return false;
    if (cancelledReasonFilter !== "all" && r.cancel_reason_code !== cancelledReasonFilter) return false;
    if (cancelledActorFilter === "employee_admin") return ["employee", "admin"].includes(r.cancelled_by_type);
    if (cancelledActorFilter !== "all") return r.cancelled_by_type === cancelledActorFilter;
    return true;
  });

  // skip_reason is free text (never a coded field) — substring match over
  // the already server-narrowed (employee/skipped-by/product/date) set.
  const skippedTasksFiltered = (wq?.skipped_tasks || []).filter((t) => {
    if (!skippedReasonFilter.trim()) return true;
    return (t.skip_reason || "").toLowerCase().includes(skippedReasonFilter.trim().toLowerCase());
  });

  const canAssign = hasCap("crm.assign");
  const canAssignConsultant = hasCap("booking.assign");
  const canAssignTask = hasCap("tasks.assign");

  const submitLeadAssign = async (userId) => {
    setAssignBusy(true);
    try {
      const r = await api.post(`/leads/assign/${leadAssignTarget.opportunity_id}`,
        { user_id: userId, expected_current_owner_user_id: leadAssignTarget.owner_user_id || null });
      if (r?.data?.already_assigned) toast.info("Already assigned to them — nothing changed");
      else toast.success("Assigned — the lead's open tasks moved to them too");
      setLeadAssignTarget(null); load();
    } catch (e) { toast.error(errDetail(e)); load(); } finally { setAssignBusy(false); }
  };

  const submitTaskAssign = async (userId, dueAtIso, reason) => {
    setAssignBusy(true);
    try {
      await api.post(`/approvals/tasks/${taskAssignTarget.task_id}/assign`,
        { user_id: userId, due_at: dueAtIso, reason });
      toast.success("Task assigned");
      setTaskAssignTarget(null); load();
    } catch (e) { toast.error(errDetail(e)); load(); } finally { setAssignBusy(false); }
  };

  const submitReassign = async (userId, alsoChangeOwner, reason) => {
    setAssignBusy(true);
    try {
      const r = await api.post(`/approvals/tasks/${reassignTarget.task_id}/reassign`,
        { user_id: userId, also_change_opportunity_owner: alsoChangeOwner, reason });
      toast.success(r.data.opportunity_owner_changed
        ? "Task reassigned — opportunity owner also changed" : "Task reassigned");
      setReassignTarget(null); load();
    } catch (e) { toast.error(errDetail(e)); load(); } finally { setAssignBusy(false); }
  };

  const submitProcessAssign = async (userId) => {
    setAssignBusy(true);
    try {
      // The one canonical Process-owner mutation (the legacy /assign route is
      // now only a deprecated server-side alias of it).
      await api.patch(`/cases/${processAssignTarget.case_id}/process-owner`,
        { process_owner_user_id: userId, expected_current_owner_user_id: processAssignTarget.owner_user_id || null });
      toast.success("Case assigned");
      setProcessAssignTarget(null); load();
    } catch (e) { toast.error(errDetail(e)); load(); } finally { setAssignBusy(false); }
  };

  const assignConsultant = async (appointmentId, counsellorUserId, expectedCurrent) => {
    if (counsellorUserId === (expectedCurrent || null)) return; // harmless no-op
    try {
      await api.patch(`/appointments/${appointmentId}/assign`,
        { counsellor_user_id: counsellorUserId, expected_current_counsellor_user_id: expectedCurrent || null });
      toast.success("Counsellor assigned");
      load();
    } catch (e) { toast.error(errDetail(e)); load(); }
  };

  const familyCode = decide?.approval?.payload?.family_product_code || decide?.approval?.product_code;
  const correctionOptions = products.filter((p) =>
    (p.code === familyCode || p.parent_code === familyCode) && !p.consultation_outcome_blocked);

  // 2026-09 R6 — the payment figures shown for a Signup-type approval are
  // ALWAYS the live, server-recomputed ones (GET /approvals/{id} ->
  // live_payment_status), fetched on every dialog open and on "Refresh".
  // sa_premium_signup used to show `approval.payload.payment_status` — the
  // snapshot taken when the approval was RAISED — so a customer who paid in
  // full afterwards still showed a shortfall and forced a needless
  // acknowledgement. The server rechecks the same live state again when the
  // decision is submitted, so nothing shown here is ever trusted by it.
  const refreshLivePaymentStatus = useCallback(async (approval) => {
    if (!approval?.approval_id) return;
    setPaymentStatusLoading(true);
    try {
      const r = await api.get(`/approvals/${approval.approval_id}`);
      setPaymentStatus(r.data.live_payment_status || null);
    } catch {
      setPaymentStatus(null);
    } finally {
      setPaymentStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    setAcknowledgeShortfall(false);
    setAcknowledgeFinancialReconciliation(false);
    setAcknowledgeGstMismatch(false);
    setConfirmedTaxTreatment("");
    setPaymentStatus(null);
    setAssignee("");
    if (decide?.approve && ["signup_case", "sa_premium_signup"].includes(decide?.approval?.type)
        && decide?.approval?.opportunity_id) {
      refreshLivePaymentStatus(decide.approval);
    }
  }, [decide, refreshLivePaymentStatus]);

  // An acknowledgement only ever applies to a shortfall that exists NOW —
  // if a refresh shows it has been paid off, the tick is cleared.
  useEffect(() => {
    if (!paymentStatus?.has_shortfall) setAcknowledgeShortfall(false);
  }, [paymentStatus]);

  const livePaymentType = decide?.approve
    && ["signup_case", "sa_premium_signup"].includes(decide?.approval?.type);

  const submit = async () => {
    try {
      const r = await api.post(`/approvals/${decide.approval.approval_id}/decide`,
        { approve: decide.approve, note,
          assignee_user_id: (decide.approve && ASSIGNEE_APPROVAL_TYPES.includes(decide.approval?.type))
            ? (assignee || null) : null,
          product_code: productOverride || null, acknowledge_shortfall: acknowledgeShortfall,
          acknowledge_financial_reconciliation: acknowledgeFinancialReconciliation,
          acknowledge_gst_mismatch: acknowledgeGstMismatch,
          confirmed_tax_treatment: confirmedTaxTreatment || null,
          clarification: !!decide.clarification, emergency_override: emergencyOverride });
      const outcome = decide.approve ? "Approved" : (decide.clarification ? "Returned for clarification" : "Rejected");
      toast.success(`${outcome}${r.data.execution_result ? ` — ${r.data.execution_result}` : ""}`);
      setDecide(null); setNote(""); setAssignee(""); setProductOverride("");
      setAcknowledgeShortfall(false); setAcknowledgeFinancialReconciliation(false);
      setAcknowledgeGstMismatch(false); setConfirmedTaxTreatment(""); setEmergencyOverride(false); load();
    } catch (e) {
      // A shortfall the caller hadn't acknowledged yet — refresh the figures
      // shown (server-computed, never trusted from a stale client fetch)
      // and require the explicit checkbox before resubmitting.
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.blocker_type === "payment_shortfall_ack_required") {
        setPaymentStatus(detail.payment_status);
        toast.error("A payment balance remains outstanding — acknowledge it below to approve anyway.");
        return;
      }
      if (detail && typeof detail === "object" && detail.blocker_type === "required_payment"
          && detail.payment_status) {
        setPaymentStatus(detail.payment_status);
        toast.error(detail.message || "No verified payment exists — this cannot be approved yet.");
        return;
      }
      if (detail && typeof detail === "object" && detail.blocker_type === "financial_reconciliation_ack_required") {
        toast.error("A verified payment exists on this opportunity — acknowledge it below to approve anyway.");
        return;
      }
      if (detail && typeof detail === "object" && detail.blocker_type === "gst_mismatch_ack_required") {
        toast.error("Source and target have different GST/reimbursement treatment — confirm the tax treatment below to approve anyway.");
        return;
      }
      toast.error(errDetail(e));
    }
  };

  if (!wq && wqError) {
    return <ErrorState testId="approvals-error" title="Couldn't load the approval centre" message={wqError} onRetry={load} />;
  }
  if (!wq) return <ListSkeleton rows={5} testId="approvals-loading" />;
  return (
    <div className="space-y-4" data-testid="approvals-page">
      {wqError && (
        <div role="alert" data-testid="approvals-stale-error"
          className="flex flex-wrap items-center gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs">
          <span>{wqError}. The lists below may be out of date.</span>
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={load}>Retry</Button>
        </div>
      )}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight flex items-center gap-2"><ShieldCheck className="h-6 w-6 text-primary" />Approval & Assignment</h1>
          <p className="text-sm text-muted-foreground">Pending approvals, ownership assignments and signup activations — consolidated for Leadership.</p>
        </div>
        <IncludeTestToggle includeTest={includeTest} canInclude={canInclude} onChange={setIncludeTest} testId="approvals-include-test" />
      </div>
      <Tabs defaultValue="pending">
        {/* R7: six tabs wrap onto extra lines instead of pushing the page wider than a phone/tablet. */}
        <TabsList data-testid="approvals-tabs-list" className="flex-wrap h-auto justify-start gap-0.5">
          <TabsTrigger data-testid="approvals-tab-pending" value="pending">Pending ({wq.pending_approvals.length})</TabsTrigger>
          {/* Round 7 Workstream E - deliberately NO single merged number here any
              more: leads, Cases, tasks and consultations are four different
              things (each is counted and labelled separately inside the tab). */}
          <TabsTrigger data-testid="approvals-tab-unassigned" value="unassigned" title="Unassigned leads, Cases, tasks and consultations - four separate counts">Unassigned</TabsTrigger>
          <TabsTrigger data-testid="approvals-tab-overdue-tasks" value="overdue_tasks">Overdue tasks ({wq.overdue_tasks_total ?? wq.overdue_tasks?.length ?? 0})</TabsTrigger>
          <TabsTrigger data-testid="approvals-tab-skipped-tasks" value="skipped_tasks">Skipped tasks ({skippedTasksFiltered.length}{wq.skipped_tasks_capped ? "+" : ""})</TabsTrigger>
          <TabsTrigger data-testid="approvals-tab-cancelled-consultations" value="cancelled_consultations">Consultation Cancellations ({cancelledConsultations.length})</TabsTrigger>
          <TabsTrigger data-testid="approvals-tab-history" value="history">History ({historyMeta.truncated && Number.isFinite(historyMeta.total) ? `${history.length} of ${historyMeta.total}+ — newest shown` : history.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="pending" className="space-y-2">
          {wq.pending_approvals.length === 0 && <EmptyState icon={ShieldCheck} title="Nothing awaiting approval" hint="Signup activations, ownership changes and product additions appear here." />}
          {wq.pending_approvals.map((a) => (
            <Card key={a.approval_id} data-testid={`approval-${a.approval_id}`}>
              <CardContent className="pt-4 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-56">
                  <div className="flex items-center gap-2 font-medium">
                    <Badge>{TYPE_LABEL[a.type] || a.type}</Badge>
                    {a.contact_id ? <Link className="underline decoration-dotted hover:text-primary" to={`/customers/${a.contact_id}`}>{a.contact_name || a.contact_id}</Link> : <span>—</span>}
                    {a.is_test_record && <TestBadge show />}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {a.product_code && <span className="mr-2">Product: {a.product_code}</span>}
                    Requested by {a.requested_by_name || a.requested_by} · {fmtIST(a.requested_at)}
                  </div>
                  {a.reason && <div className="text-sm mt-1">{a.reason}</div>}
                  {a.type === "sa_premium_signup" && a.live_payment_status && (
                    <div className="text-xs text-muted-foreground mt-1" data-testid={`approval-live-payment-${a.approval_id}`}>
                      {a.reason ? "Figures above are from when this was requested. " : ""}
                      Now: {inr(a.live_payment_status.paid)} of {inr(a.live_payment_status.expected)} verified
                      {a.live_payment_status.has_shortfall
                        ? ` — ${inr(a.live_payment_status.outstanding)} outstanding`
                        : a.live_payment_status.zero_payment ? " — no verified payment" : " — fully paid"}
                    </div>
                  )}
                  {a.type === "customer_merge" && a.payload?.preview && (
                    <div className="text-xs text-muted-foreground mt-1">
                      Merging <b>{a.payload.preview.source.full_name}</b> ({a.payload.preview.source.customer_id}) into{" "}
                      <b>{a.payload.preview.target.full_name}</b> ({a.payload.preview.target.customer_id})
                      {a.payload.preview.conflicts.length > 0 && (
                        <span className="ml-1.5 text-amber-600 dark:text-amber-400">
                          · {a.payload.preview.conflicts.length} thing{a.payload.preview.conflicts.length === 1 ? "" : "s"} to review
                        </span>
                      )}
                    </div>
                  )}
                  {a.type === "product_change" && a.payload?.new_product_code && (
                    <div className="text-xs text-muted-foreground mt-1">
                      Change to <b>{a.payload.new_product_code}</b>
                      {a.payload.requires_financial_reconciliation_ack && (
                        <span className="ml-1.5 text-amber-600 dark:text-amber-400">
                          · a verified payment exists — requires finance.config to approve
                        </span>
                      )}
                    </div>
                  )}
                  {a.type === "discontinue_and_replace" && a.payload?.new_product_code && (
                    <div className="text-xs text-muted-foreground mt-1">
                      Discontinue this opportunity and create a new one for <b>{a.payload.new_product_code}</b>
                      {a.payload.destination_selectable === false && (
                        <span className="ml-1.5 text-red-600 dark:text-red-400">· {a.payload.destination_reason}</span>
                      )}
                    </div>
                  )}
                  {a.type === "task_completion" && (
                    <div className="text-xs mt-1 space-y-0.5" data-testid={`task-completion-context-${a.approval_id}`}>
                      <div>
                        <b>{a.payload?.task_title}</b>
                        {a.payload?.is_mandatory && <span className="ml-1.5 uppercase text-[10px] text-[hsl(var(--warning))] font-semibold">Mandatory</span>}
                        {a.payload?.resubmission && <Badge variant="outline" className="ml-1.5 text-[9px]">Resubmission</Badge>}
                      </div>
                      {a.payload?.due_at && (
                        <div className="text-muted-foreground">Due {fmtIST(a.payload.due_at)}</div>
                      )}
                      {a.payload?.assigned_to_name && (
                        <div className="text-muted-foreground">Assigned to {a.payload.assigned_to_name}</div>
                      )}
                      <div className="text-muted-foreground">Employee note: {a.payload?.note}</div>
                      {a.requested_by === user?.user_id && (
                        <div className="text-amber-600 dark:text-amber-400 font-medium">
                          You submitted this — only another authorised approver (or an admin
                          emergency override) can decide it.
                        </div>
                      )}
                    </div>
                  )}
                  {a.type === "payment_reallocation" && a.payload?.amount && (
                    <div className="text-xs text-muted-foreground mt-1">
                      Reallocate {inr(a.payload.amount)} from {a.product_code} to {a.payload.target_opportunity_id ? "another opportunity" : "—"}
                      {a.payload.gst_mismatch && (
                        <span className="ml-1.5 text-amber-600 dark:text-amber-400">
                          · GST/reimbursement rates differ ({a.payload.source_gst_rate} vs {a.payload.target_gst_rate}) — needs confirmation
                        </span>
                      )}
                    </div>
                  )}
                </div>
                {(() => {
                  const allowedRoles = APPROVAL_TYPE_ROLE_POLICY[a.type];
                  const roleBlocked = allowedRoles && !allowedRoles.includes(user?.role);
                  if (roleBlocked) {
                    return (
                      <div className="text-xs text-muted-foreground text-right max-w-40">
                        Only {allowedRoles.join(" or ")} can decide this
                      </div>
                    );
                  }
                  return (
                    <div className="flex gap-2">
                      <Button size="sm" data-testid={`approve-${a.approval_id}`} onClick={() => { setDecide({ approval: a, approve: true }); setProductOverride(a.product_code || ""); }}><CheckCircle2 className="h-4 w-4 mr-1" />Approve</Button>
                      <Button size="sm" variant="outline" data-testid={`reject-${a.approval_id}`} onClick={() => setDecide({ approval: a, approve: false, clarification: false })}><XCircle className="h-4 w-4 mr-1" />Reject</Button>
                      {a.type === "task_completion" && (
                        <Button size="sm" variant="outline" data-testid={`clarify-${a.approval_id}`}
                          onClick={() => setDecide({ approval: a, approve: false, clarification: true })}>
                          Return for clarification
                        </Button>
                      )}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          ))}
        </TabsContent>
        <TabsContent value="unassigned" className="space-y-5">
          {/* Four DIFFERENT numbers, each from the same filter its list uses
              (backend unassigned_queues.py) - never summed, never mixed. */}
          {wq.unassigned_summary && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2" data-testid="unassigned-summary">
              {[
                ["leads", wq.unassigned_summary.unassigned_leads, "Active opportunities with no owner (the Lead Pool). Cleared by Claim / Assign."],
                ["cases", wq.unassigned_summary.unassigned_cases, "Open Process Cases with no Process owner. Cleared by assigning a Process owner."],
                ["tasks", wq.unassigned_summary.unassigned_tasks, "Open tasks with no assignee. A task follows its lead's owner when the lead is assigned - except tasks with no opportunity (e.g. classify a new lead), which need assigning here."],
                ["consultations", wq.unassigned_summary.unassigned_consultations, "Upcoming consultations with no counsellor."],
              ].map(([key, blk, hint]) => (
                <Card key={key} className="p-3" title={hint}>
                  <div className="text-xs text-muted-foreground">{blk.label}</div>
                  <div className="text-xl font-semibold tabular-nums" data-testid={`unassigned-summary-${key}`}>{formatCount(blk)}</div>
                  {truncationNote(blk) && <div className="text-[10px] text-muted-foreground">{truncationNote(blk)}</div>}
                  {key === "tasks" && blk.without_opportunity > 0 && (
                    <div className="text-[10px] text-muted-foreground" data-testid="unassigned-summary-tasks-noopp">{blk.without_opportunity} have no opportunity - assigning a lead does not clear them</div>
                  )}
                  {wq.unassigned_summary.test_records && !wq.include_test && wq.unassigned_summary.test_records[`unassigned_${key}`] > 0 && (
                    <div className="text-[10px] text-muted-foreground">+{wq.unassigned_summary.test_records[`unassigned_${key}`]} TEST hidden</div>
                  )}
                </Card>
              ))}
            </div>
          )}
          <div className="space-y-2">
            <SectionHeader icon={ClipboardList} title="Unassigned tasks"
              count={wq.unassigned_summary ? formatCount(wq.unassigned_summary.unassigned_tasks) : (wq.unassigned_tasks?.length || 0)}
              note={truncationNote(wq.unassigned_summary?.unassigned_tasks)}
              collapsed={!!collapsed.tasks} onToggle={() => toggleCollapsed("tasks")} testId="unassigned-tasks" />
            {!collapsed.tasks && ((wq.unassigned_tasks?.length || 0) === 0
              ? <EmptyState icon={ClipboardList} title="No unassigned tasks" hint="Human follow-up tasks with no owner (AI Voice handoff, missed consultation, payment follow-up) appear here." />
              : wq.unassigned_tasks.map((t) => (
                <Card key={t.task_id} data-testid={`unassigned-task-${t.task_id}`}>
                  <CardContent className="pt-4 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm">
                    <span className="font-medium">{t.reason}</span>
                    {t.contact_id
                      ? <Link className="underline decoration-dotted hover:text-primary" to={t.customer_360_link || `/customers/${t.contact_id}`}>{t.contact_name || t.contact_id}</Link>
                      : <span className="text-muted-foreground">No customer linked</span>}
                    {(t.product_name || t.product_code) && <Badge variant="outline">{t.product_name || t.product_code}</Badge>}
                    {t.customer_id && <span className="text-xs text-muted-foreground">{t.customer_id}</span>}
                    <span className="text-xs text-muted-foreground">Created {fmtIST(t.created_at)}</span>
                    <span className="text-xs text-muted-foreground">Due {fmtIST(t.effective_due_at || t.due_at)}</span>
                    {t.waiting_on_opportunity && (
                      <Badge variant="outline" className="text-[9px]" title="This task belongs to a lead intake that has no opportunity yet, so assigning a lead does not clear it - assign it here.">
                        No opportunity yet
                      </Badge>
                    )}
                    {t.opportunity_owner_name && (
                      <Badge variant="outline" className="text-[9px]" title="The lead now has an owner — this row should clear shortly, or assign it to them.">
                        Lead owner: {t.opportunity_owner_name}
                      </Badge>
                    )}
                    {t.is_test_record && <TestBadge show />}
                    {canAssignTask
                      ? <Button size="sm" className="ml-auto h-7 text-xs" data-testid={`unassigned-task-assign-${t.task_id}`}
                          onClick={() => setTaskAssignTarget(t)}>Assign</Button>
                      : <span className="text-xs text-muted-foreground ml-auto">Not authorised to assign</span>}
                  </CardContent>
                </Card>
              )))}
          </div>
          <div className="space-y-2">
            <SectionHeader icon={UserPlus} title="Unassigned leads"
              count={wq.unassigned_summary ? formatCount(wq.unassigned_summary.unassigned_leads) : wq.unassigned_leads.length}
              note={truncationNote(wq.unassigned_summary?.unassigned_leads)}
              sortOptions={[["newest", "Newest first"], ["oldest", "Oldest first"]]}
              sort={leadSort} onSortChange={setLeadSort}
              collapsed={!!collapsed.leads} onToggle={() => toggleCollapsed("leads")} testId="unassigned-leads" />
            {!collapsed.leads && (wq.unassigned_leads.length === 0
              ? <EmptyState icon={UserPlus} title="No unassigned leads" hint="Leads without an owner appear here for assignment." />
              : wq.unassigned_leads.map((o) => (
                <Card key={o.opportunity_id} data-testid={`unassigned-lead-${o.opportunity_id}`}>
                  <CardContent className="pt-4 flex flex-wrap items-center gap-3 text-sm">
                    <Link className="font-medium underline decoration-dotted hover:text-primary" to={`/customers/${o.contact_id}`}>{o.contact_name || o.contact_id}</Link>
                    <Badge variant="outline">{o.product_name || o.product_code}</Badge>
                    <span className="text-xs text-muted-foreground">{o.customer_id}</span>
                    <span className="text-xs text-muted-foreground">Entered {fmtIST(o.created_at)}</span>
                    {canAssign && (
                      <Button size="sm" className="ml-auto h-7 text-xs" data-testid={`unassigned-lead-assign-${o.opportunity_id}`}
                        onClick={() => setLeadAssignTarget(o)}>Assign</Button>
                    )}
                  </CardContent>
                </Card>
              )))}
          </div>
          <div className="space-y-2">
            <SectionHeader icon={CalendarClock} title="Unassigned consultations"
              count={wq.unassigned_summary ? formatCount(wq.unassigned_summary.unassigned_consultations) : wq.unassigned_consultations.length}
              note={truncationNote(wq.unassigned_summary?.unassigned_consultations)}
              sortOptions={[["soonest", "Soonest first"], ["latest", "Latest first"]]}
              sort={consultationSort} onSortChange={setConsultationSort}
              collapsed={!!collapsed.consultations} onToggle={() => toggleCollapsed("consultations")} testId="unassigned-consultations" />
            {!collapsed.consultations && (wq.unassigned_consultations.length === 0
              ? <EmptyState icon={CalendarClock} title="No unassigned consultations" hint="Upcoming consultations without a counsellor appear here." />
              : wq.unassigned_consultations.map((a) => (
                <Card key={a.appointment_id} data-testid={`unassigned-consultation-${a.appointment_id}`}>
                  <CardContent className="pt-4 flex flex-wrap items-center gap-3 text-sm">
                    <Link className="font-medium underline decoration-dotted hover:text-primary" to={`/customers/${a.contact_id}`}>{a.contact_name || a.contact_id}</Link>
                    <Badge variant="outline">{a.product_code}</Badge>
                    {a.appointment_type_name && <span className="text-xs text-muted-foreground">{a.appointment_type_name}</span>}
                    <span className="text-sm font-medium text-[hsl(var(--warning))]">{fmtIST(a.scheduled_start)}</span>
                    <Badge variant="outline" className="text-[9px]">Unassigned</Badge>
                    {canAssignConsultant ? (
                      <Select onValueChange={(v) => assignConsultant(a.appointment_id, v, a.counsellor_user_id || null)}>
                        <SelectTrigger data-testid={`unassigned-consultation-assign-${a.appointment_id}`} className="h-8 text-xs w-44 ml-auto"><SelectValue placeholder="Assign consultant…" /></SelectTrigger>
                        <SelectContent>
                          {counsellors.map((c) => <SelectItem key={c.user_id} value={c.user_id}>{c.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : <span className="text-xs text-muted-foreground ml-auto">Not authorised to assign</span>}
                  </CardContent>
                </Card>
              )))}
          </div>
          <div className="space-y-2">
            <SectionHeader icon={Briefcase} title="Unassigned Cases"
              count={wq.unassigned_summary ? formatCount(wq.unassigned_summary.unassigned_cases) : wq.unassigned_process_leads.length}
              note={truncationNote(wq.unassigned_summary?.unassigned_cases)}
              sortOptions={[["waiting_longest", "Waiting longest"], ["newest", "Newest"]]}
              sort={processSort} onSortChange={setProcessSort}
              collapsed={!!collapsed.process} onToggle={() => toggleCollapsed("process")} testId="unassigned-process" />
            {!collapsed.process && (wq.unassigned_process_leads.length === 0
              ? <EmptyState icon={Briefcase} title="No unassigned process cases" hint="Signed-up cases without an owner appear here." />
              : wq.unassigned_process_leads.map((c) => (
                <Card key={c.case_id} data-testid={`unassigned-case-${c.case_id}`}>
                  <CardContent className="pt-4 flex flex-wrap items-center gap-3 text-sm">
                    <Link className="font-medium underline decoration-dotted hover:text-primary" to={`/customers/${c.contact_id}`}>{c.contact_name || c.contact_id}</Link>
                    <Badge variant="outline">{c.product_code}</Badge>
                    <span className="text-xs text-muted-foreground">{c.current_stage}</span>
                    <span className="text-xs text-muted-foreground">Waiting since {fmtIST(c.created_at)}</span>
                    {canAssign && (
                      <Button size="sm" className="ml-auto h-7 text-xs" data-testid={`unassigned-case-assign-${c.case_id}`}
                        onClick={() => setProcessAssignTarget(c)}>Assign</Button>
                    )}
                  </CardContent>
                </Card>
              )))}
          </div>

          {/* Past unassigned consultations — deliberately its own section,
              never mixed into "Pending consultations" above (that one is
              upcoming/still-actionable-by-assigning; this one is about
              what already happened while unassigned, and what recovery
              action followed). Bounded to the last `past_consultation_days`
              (default 30) rather than rendering unbounded history. */}
          <div className="space-y-2">
            <SectionHeader icon={History} title="Past unassigned consultations" count={wq.past_unassigned_consultations.length}
              sortOptions={[["unresolved_first", "Unresolved first"], ["oldest", "Oldest first"], ["newest", "Newest first"]]}
              sort={pastConsultationSort} onSortChange={setPastConsultationSort}
              collapsed={!!collapsed.pastConsultations} onToggle={() => toggleCollapsed("pastConsultations")} testId="past-unassigned-consultations" />
            {!collapsed.pastConsultations && (wq.past_unassigned_consultations.length === 0
              ? <EmptyState icon={History} title="No past unassigned consultations" hint="Consultations whose time passed while no consultant was assigned appear here, along with what happened afterward." />
              : wq.past_unassigned_consultations.map((r) => (
                <Card key={r.appointment_id} data-testid={`past-unassigned-consultation-${r.appointment_id}`}>
                  <CardContent className="pt-4 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm">
                    <Link className="font-medium underline decoration-dotted hover:text-primary" to={`/customers/${r.contact_id}`}>{r.contact_name || r.contact_id}</Link>
                    <Badge variant="outline">{r.product_code}</Badge>
                    <span className="text-xs text-muted-foreground">Was {fmtIST(r.scheduled_start)}</span>
                    <span className="text-xs text-muted-foreground">Appt {r.appointment_id.slice(0, 8)}</span>
                    <Badge variant="outline" className="text-[9px] capitalize">{r.status}</Badge>
                    {!r.ever_assigned && <span className="text-xs text-muted-foreground">Never had a consultant assigned</span>}
                    {r.cancel_reason && <span className="text-xs text-muted-foreground" title={r.cancel_reason}>Cancelled: {r.cancel_reason.length > 60 ? `${r.cancel_reason.slice(0, 60)}…` : r.cancel_reason}</span>}
                    <Badge className={PAST_CONSULT_BADGE[r.recovery_state] || ""} data-testid={`past-unassigned-consultation-state-${r.appointment_id}`}>{r.recovery_label}</Badge>
                    {r.recovery_task_owner_name && <span className="text-xs text-muted-foreground">Recovery task: {r.recovery_task_owner_name}</span>}
                    {r.rebooked_scheduled_start && <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">New: {fmtIST(r.rebooked_scheduled_start)}</span>}
                    {r.is_test_record && <TestBadge show />}
                    <Link className="ml-auto text-xs underline decoration-dotted hover:text-primary" to={`/customers/${r.contact_id}`}>Open customer</Link>
                  </CardContent>
                </Card>
              )))}
          </div>

          <AssignDialog open={!!leadAssignTarget} setOpen={(o) => !o && setLeadAssignTarget(null)}
            title={`Assign — ${leadAssignTarget?.product_name || leadAssignTarget?.product_code || ""}`}
            employees={salesEmployees} onConfirm={submitLeadAssign} busy={assignBusy}
            testIdPrefix="unassigned-lead-assign" />
          <AssignDialog open={!!processAssignTarget} setOpen={(o) => !o && setProcessAssignTarget(null)}
            title={`Assign Process Case — ${processAssignTarget?.product_code || ""}`}
            employees={processEmployees} onConfirm={submitProcessAssign} busy={assignBusy}
            testIdPrefix="unassigned-case-assign" />
          <TaskAssignDialog open={!!taskAssignTarget} setOpen={(o) => !o && setTaskAssignTarget(null)}
            task={taskAssignTarget} employees={salesEmployees} onConfirm={submitTaskAssign} busy={assignBusy} />
        </TabsContent>
        {/* 2026-09 Phase 2 (C1) — read-only leadership visibility, same
            pattern as "Past unassigned consultations": derived from
            canonical fields (core.effective_task_due_at/is_task_overdue),
            never a second status system. No reminder/escalation count —
            audited, nothing like that is persisted on a task anywhere. */}
        <TabsContent value="overdue_tasks" className="space-y-3">
          {/* 2026-09 stabilization (Workstream C) — this tab counts EMPLOYEE
              delinquency only: TEST records are excluded, and provider /
              system failures are reported separately (Tasks -> System /
              provider failures), never as employee overdue work. */}
          {wq.task_counts && (
            <p className="text-xs text-muted-foreground" data-testid="overdue-tasks-scope-note">
              Employee overdue tasks only — TEST records are excluded ({wq.task_counts.population?.test?.employee_overdue ?? 0} TEST overdue not shown) and{" "}
              <Link to="/tasks?view=system" className="underline">{wq.task_counts.system_failure?.overdue ?? 0} system / provider failure task(s)</Link>{" "}
              are reported separately.
            </p>
          )}
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Assigned employee</Label>
              <Select value={overdueEmployeeFilter} onValueChange={setOverdueEmployeeFilter}>
                <SelectTrigger data-testid="overdue-filter-employee" className="h-8 text-xs w-44"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All employees</SelectItem>
                  {users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Product</Label>
              <Select value={overdueProductFilter} onValueChange={setOverdueProductFilter}>
                <SelectTrigger data-testid="overdue-filter-product" className="h-8 text-xs w-44"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All products</SelectItem>
                  {products.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Due from</Label>
              <Input type="date" data-testid="overdue-filter-due-from" className="h-8 text-xs w-36"
                value={overdueDueFrom} onChange={(e) => setOverdueDueFrom(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Due to</Label>
              <Input type="date" data-testid="overdue-filter-due-to" className="h-8 text-xs w-36"
                value={overdueDueTo} onChange={(e) => setOverdueDueTo(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Sort</Label>
              <Select value={overdueSort} onValueChange={setOverdueSort}>
                <SelectTrigger data-testid="overdue-filter-sort" className="h-8 text-xs w-44"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="oldest">Oldest overdue first</SelectItem>
                  <SelectItem value="newest">Newest overdue first</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(overdueEmployeeFilter !== "all" || overdueProductFilter !== "all" || overdueDueFrom || overdueDueTo) && (
              <Button size="sm" variant="ghost" className="h-8 text-xs" data-testid="overdue-filter-clear"
                onClick={() => { setOverdueEmployeeFilter("all"); setOverdueProductFilter("all"); setOverdueDueFrom(""); setOverdueDueTo(""); }}>
                Clear filters</Button>
            )}
          </div>
          {(wq.overdue_tasks_total ?? wq.overdue_tasks?.length ?? 0) === 0
            ? <EmptyState icon={Clock} title="No overdue tasks match these filters" hint="Open tasks past their effective due date, across every employee, appear here." />
            : <>
              {wq.overdue_tasks.map((t) => (
                <Card key={t.task_id} data-testid={`overdue-task-${t.task_id}`}>
                  <CardContent className="pt-4 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm">
                    <span className="font-medium">{t.title}</span>
                    {t.task_type && <Badge variant="outline" className="text-[10px]">{t.task_type.replace(/_/g, " ")}</Badge>}
                    {t.contact_id
                      ? <Link className="underline decoration-dotted hover:text-primary" to={t.customer_360_link || `/customers/${t.contact_id}`}>{t.contact_name || t.contact_id}</Link>
                      : <span className="text-muted-foreground">No customer linked</span>}
                    {(t.product_name || t.product_code) && <Badge variant="outline">{t.product_name || t.product_code}</Badge>}
                    <span className="text-xs text-muted-foreground">Assigned to {t.owner_name || t.owner_user_id}</span>
                    <span className="text-xs text-muted-foreground">Due {fmtIST(t.effective_due_at)}</span>
                    <Badge className="bg-red-500/15 text-red-600 dark:text-red-400" data-testid={`overdue-task-amount-${t.task_id}`}>
                      {t.overdue_days >= 1 ? `${t.overdue_days}d overdue` : `${t.overdue_hours}h overdue`}
                    </Badge>
                    {t.is_test_record && <TestBadge show />}
                    {canAssignTask && (
                      <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`overdue-task-reassign-${t.task_id}`}
                        onClick={() => setReassignTarget(t)}>Reassign</Button>
                    )}
                    <Link className="text-xs underline decoration-dotted hover:text-primary" to={t.customer_360_link || `/customers/${t.contact_id}`}>Open customer</Link>
                  </CardContent>
                </Card>
              ))}
              <div className="flex items-center gap-2 pt-1">
                <Button size="sm" variant="outline" data-testid="overdue-tasks-prev-page"
                  disabled={overduePage <= 1} onClick={() => setOverduePage((p) => Math.max(1, p - 1))}>Prev</Button>
                <span className="text-xs text-muted-foreground" data-testid="overdue-tasks-page-label">
                  Page {wq.overdue_tasks_page || 1} · {wq.overdue_tasks_total ?? 0} total overdue
                </span>
                <Button size="sm" variant="outline" data-testid="overdue-tasks-next-page"
                  disabled={!wq.overdue_tasks_has_more} onClick={() => setOverduePage((p) => p + 1)}>Next</Button>
              </div>
            </>}
          <ReassignTaskDialog open={!!reassignTarget} setOpen={(o) => !o && setReassignTarget(null)}
            task={reassignTarget} employees={salesEmployees} onConfirm={submitReassign} busy={assignBusy} />
        </TabsContent>

        {/* 2026-09 Phase 2 (C2) — surfaces Phase 1's skip fields directly.
            Never auto-converts a skip to completed, no scoring here. */}
        <TabsContent value="skipped_tasks" className="space-y-3">
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Assigned employee</Label>
              <Select value={skippedEmployeeFilter} onValueChange={setSkippedEmployeeFilter}>
                <SelectTrigger data-testid="skipped-filter-employee" className="h-8 text-xs w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All employees</SelectItem>
                  {users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Skipped by</Label>
              <Select value={skippedByFilter} onValueChange={setSkippedByFilter}>
                <SelectTrigger data-testid="skipped-filter-by" className="h-8 text-xs w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Anyone</SelectItem>
                  {users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Product</Label>
              <Select value={skippedProductFilter} onValueChange={setSkippedProductFilter}>
                <SelectTrigger data-testid="skipped-filter-product" className="h-8 text-xs w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All products</SelectItem>
                  {products.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Skipped from</Label>
              <Input type="date" data-testid="skipped-filter-from" className="h-8 text-xs w-36"
                value={skippedFrom} onChange={(e) => setSkippedFrom(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Skipped to</Label>
              <Input type="date" data-testid="skipped-filter-to" className="h-8 text-xs w-36"
                value={skippedTo} onChange={(e) => setSkippedTo(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Reason contains</Label>
              <Input data-testid="skipped-filter-reason" className="h-8 text-xs w-40" placeholder="Search reason…"
                value={skippedReasonFilter} onChange={(e) => setSkippedReasonFilter(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Sort</Label>
              <Select value={skippedSort} onValueChange={setSkippedSort}>
                <SelectTrigger data-testid="skipped-filter-sort" className="h-8 text-xs w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest skipped first</SelectItem>
                  <SelectItem value="oldest">Oldest skipped first</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(skippedEmployeeFilter !== "all" || skippedByFilter !== "all" || skippedProductFilter !== "all"
              || skippedFrom || skippedTo || skippedReasonFilter) && (
              <Button size="sm" variant="ghost" className="h-8 text-xs" data-testid="skipped-filter-clear"
                onClick={() => {
                  setSkippedEmployeeFilter("all"); setSkippedByFilter("all"); setSkippedProductFilter("all");
                  setSkippedFrom(""); setSkippedTo(""); setSkippedReasonFilter("");
                }}>Clear filters</Button>
            )}
          </div>
          {skippedTasksFiltered.length === 0
            ? <EmptyState icon={SkipForward} title="No skipped tasks match these filters" hint="Tasks employees have skipped, with their reason, appear here." />
            : skippedTasksFiltered.map((t) => (
              <Card key={t.task_id} data-testid={`skipped-task-${t.task_id}`}>
                <CardContent className="pt-4 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm">
                  <span className="font-medium">{t.title}</span>
                  {t.contact_id
                    ? <Link className="underline decoration-dotted hover:text-primary" to={t.customer_360_link || `/customers/${t.contact_id}`}>{t.contact_name || t.contact_id}</Link>
                    : <span className="text-muted-foreground">No customer linked</span>}
                  {(t.product_name || t.product_code) && <Badge variant="outline">{t.product_name || t.product_code}</Badge>}
                  <span className="text-xs text-muted-foreground">Assigned: {t.owner_name || "—"}</span>
                  <span className="text-xs text-muted-foreground">By {t.skipped_by_name || t.skipped_by}</span>
                  <span className="text-xs text-muted-foreground">{fmtIST(t.skipped_at)}</span>
                  {t.skip_reason && <span className="text-xs" title={t.skip_reason}>Reason: {t.skip_reason.length > 80 ? `${t.skip_reason.slice(0, 80)}…` : t.skip_reason}</span>}
                  {t.skip_note && <span className="text-xs text-muted-foreground" title={t.skip_note}>Note: {t.skip_note.length > 60 ? `${t.skip_note.slice(0, 60)}…` : t.skip_note}</span>}
                  {t.is_test_record && <TestBadge show />}
                  <Link className="ml-auto text-xs underline decoration-dotted hover:text-primary" to={t.customer_360_link || `/customers/${t.contact_id}`}>Open customer</Link>
                </CardContent>
              </Card>
            ))}
        </TabsContent>

        {/* 2026-09 Step 1 — Consultation Cancellations. Read-only Leadership
            visibility, same pattern as Overdue/Skipped tasks and Past
            unassigned consultations: derived directly from services.
            cancel_appointment's own structured fields, never a db.approvals
            record (nothing here is a decision to approve/reject). Default
            actor filter prioritizes employee/consultant cancellations (this
            tab's primary purpose) — "All" is one click away. */}
        <TabsContent value="cancelled_consultations" className="space-y-3">
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Consultant</Label>
              <Select value={cancelledConsultantFilter} onValueChange={setCancelledConsultantFilter}>
                <SelectTrigger data-testid="cancelled-consult-filter-consultant" className="h-8 text-xs w-44"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All consultants</SelectItem>
                  {counsellors.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Cancelled from</Label>
              <Input type="date" data-testid="cancelled-consult-filter-from" className="h-8 text-xs w-36"
                value={cancelledFrom} onChange={(e) => setCancelledFrom(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Cancelled to</Label>
              <Input type="date" data-testid="cancelled-consult-filter-to" className="h-8 text-xs w-36"
                value={cancelledTo} onChange={(e) => setCancelledTo(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Reason</Label>
              <Select value={cancelledReasonFilter} onValueChange={setCancelledReasonFilter}>
                <SelectTrigger data-testid="cancelled-consult-filter-reason" className="h-8 text-xs w-48"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All reasons</SelectItem>
                  {cancelReasons.map((r) => <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Cancelled by</Label>
              <Select value={cancelledActorFilter} onValueChange={setCancelledActorFilter}>
                <SelectTrigger data-testid="cancelled-consult-filter-actor" className="h-8 text-xs w-48"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="employee_admin">Consultant / Leadership (default)</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="employee">Consultant only</SelectItem>
                  <SelectItem value="admin">Leadership / Admin only</SelectItem>
                  <SelectItem value="customer">Customer</SelectItem>
                  <SelectItem value="system">System</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(cancelledConsultantFilter !== "all" || cancelledReasonFilter !== "all"
              || cancelledActorFilter !== "employee_admin" || cancelledFrom || cancelledTo) && (
              <Button size="sm" variant="ghost" className="h-8 text-xs" data-testid="cancelled-consult-filter-clear"
                onClick={() => {
                  setCancelledConsultantFilter("all"); setCancelledReasonFilter("all");
                  setCancelledActorFilter("employee_admin"); setCancelledFrom(""); setCancelledTo("");
                }}>Clear filters</Button>
            )}
          </div>
          {cancelledConsultations.length === 0
            ? <EmptyState icon={XCircle} title="No cancellations match these filters" hint="Consultation cancellations — who cancelled, when, how long before the consultation, and why — appear here." />
            : cancelledConsultations.map((r) => (
              <Card key={r.appointment_id} data-testid={`cancelled-consultation-${r.appointment_id}`}>
                <CardContent className="pt-4 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm">
                  <Link className="font-medium underline decoration-dotted hover:text-primary" to={`/customers/${r.contact_id}`}>{r.contact_name || r.contact_id}</Link>
                  {(r.product_name || r.product_code) && <Badge variant="outline">{r.product_name || r.product_code}</Badge>}
                  <span className="text-xs text-muted-foreground">Consultant: {r.counsellor_name || "Unassigned"}</span>
                  <span className="text-xs text-muted-foreground">Was {fmtIST(r.scheduled_start)}</span>
                  <span className="text-xs text-muted-foreground">Cancelled {fmtIST(r.cancelled_at)}</span>
                  <Badge className={r.seconds_before_consultation < 0
                    ? "bg-red-500/15 text-red-600 dark:text-red-400"
                    : "bg-slate-500/15 text-slate-600 dark:text-slate-400"}
                    data-testid={`cancelled-consultation-timing-${r.appointment_id}`}>{r.timing_label}</Badge>
                  <Badge className={CANCEL_ACTOR_BADGE[r.cancelled_by_type] || ""} data-testid={`cancelled-consultation-actor-${r.appointment_id}`}>
                    {CANCEL_ACTOR_LABEL[r.cancelled_by_type] || r.cancelled_by_type || "Unknown"}
                  </Badge>
                  <span className="text-xs text-muted-foreground">By {r.cancelled_by_name || "—"}</span>
                  {r.cancel_reason && <span className="text-xs" title={r.cancel_reason}>Reason: {r.cancel_reason}</span>}
                  {r.cancellation_note && <span className="text-xs text-muted-foreground" title={r.cancellation_note}>Note: {r.cancellation_note.length > 60 ? `${r.cancellation_note.slice(0, 60)}…` : r.cancellation_note}</span>}
                  {typeof r.call_attempts_before_cancellation === "number" && (
                    <span className="text-xs text-muted-foreground">
                      {r.call_attempts_before_cancellation === 0 ? "No call attempts logged before cancellation"
                        : `${r.call_attempts_before_cancellation} call attempt${r.call_attempts_before_cancellation === 1 ? "" : "s"} before cancellation`}
                    </span>
                  )}
                  {r.is_test_record && <TestBadge show />}
                  <Link className="ml-auto text-xs underline decoration-dotted hover:text-primary" to={`/customers/${r.contact_id}`}>Open customer</Link>
                </CardContent>
              </Card>
            ))}
        </TabsContent>

        <TabsContent value="history" className="space-y-2">
          {history.map((a) => a.type === "product_activation" ? (
            // 2026-09 P2 — never a Leadership decision (status is never
            // "pending"/"approved"/"rejected"), so this card is display-
            // only: kind distinguishes a genuine outstanding Commercial
            // Plan payment (financial_prerequisite) from an automatic
            // activation (auto_activation) from an unexpected error
            // (activation_failure) — never the generic Skill Migration
            // "Signup payment outstanding" wording.
            <div key={a.approval_id} data-testid={`product-activation-${a.approval_id}`}
                className="rounded-md border px-3 py-2 text-sm flex flex-wrap items-center gap-2">
              <Badge className={PRODUCT_ACTIVATION_BADGE_CLASS[a.status] || "bg-muted text-muted-foreground"}>
                {PRODUCT_ACTIVATION_KIND_LABEL[a.kind] || a.kind}</Badge>
              <span>{a.contact_name}</span>
              {a.product_code && <span className="text-xs text-muted-foreground">{a.product_code}</span>}
              <span className="text-xs text-muted-foreground">{fmtIST(a.requested_at)}</span>
              {a.reason && <span className="w-full text-xs text-muted-foreground">{a.reason}</span>}
            </div>
          ) : (
            <div key={a.approval_id} className="rounded-md border px-3 py-2 text-sm flex flex-wrap items-center gap-2">
              <Badge className={a.status === "approved" ? "bg-emerald-500/15 text-emerald-600" : "bg-red-500/15 text-red-600"}>{a.status}</Badge>
              <span className="font-medium">{TYPE_LABEL[a.type] || a.type}</span>
              <span>{a.contact_name}</span>
              <span className="text-xs text-muted-foreground">by {a.decided_by_name || a.decided_by} · {fmtIST(a.decided_at)}</span>
              {a.handoff_status === "pending_payment" && (
                <Badge className="bg-amber-500/15 text-amber-600" data-testid={`handoff-pending-${a.approval_id}`}>
                  Case pending — signup payment outstanding</Badge>
              )}
              {a.execution_result && <span className="text-xs text-muted-foreground">→ {a.execution_result}</span>}
            </div>
          ))}
        </TabsContent>
      </Tabs>
      <Dialog open={!!decide} onOpenChange={(o) => !o && setDecide(null)}>
        <DialogContent data-testid="decide-dialog" className={decide?.approval?.type === "customer_merge" ? "max-w-2xl" : undefined}>
          <DialogHeader><DialogTitle>{decide?.approve ? "Approve" : (decide?.clarification ? "Return for clarification" : "Reject")} — {TYPE_LABEL[decide?.approval?.type] || ""}</DialogTitle></DialogHeader>
          {decide?.approval?.type === "customer_merge" && decide?.approval?.payload?.preview && (
            <MergePreviewCard preview={decide.approval.payload.preview} />
          )}
          <Textarea data-testid="decision-note" placeholder="Decision note (audited)" value={note} onChange={(e) => setNote(e.target.value)} />
          {decide?.approve && decide?.approval?.type === "signup_case" && paymentStatus?.zero_payment && (
            <div className="rounded-md border border-red-500/40 bg-red-500/10 p-3 text-xs space-y-1" data-testid="zero-payment-warning">
              <div className="flex items-center gap-1.5 font-medium text-red-600 dark:text-red-400">
                <AlertTriangle className="h-3.5 w-3.5" />No core/registration payment received
              </div>
              <p className="text-muted-foreground">You can still approve this decision — it will be recorded immediately — but Process handoff cannot proceed until some core or Registration Fee payment is received. The case will remain pending.</p>
            </div>
          )}
          {decide?.approve && decide?.approval?.type === "signup_case" && paymentStatus?.has_shortfall && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs space-y-2" data-testid="payment-shortfall-warning">
              <div className="flex items-center gap-1.5 font-medium text-amber-600 dark:text-amber-400">
                <AlertTriangle className="h-3.5 w-3.5" />PAYMENT SHORTFALL
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 font-mono tabular-nums">
                <span>Commercial Plan total:</span><span>{inr(paymentStatus.commercial_plan_total)}</span>
                <span>Signup / core obligation:</span><span>{inr(paymentStatus.core_expected)}</span>
                <span>Registration received:</span><span>{inr(paymentStatus.registration_received)}</span>
                <span>Core instalment paid:</span><span>{inr(paymentStatus.core_paid)}</span>
                <span className="font-medium">Total relevant payment received:</span><span className="font-medium">{inr(paymentStatus.total_core_received)}</span>
                <span className="font-medium">Outstanding:</span><span className="font-medium">{inr(paymentStatus.shortfall)}</span>
              </div>
              <p>You are approving Process handoff while a payment balance remains outstanding.</p>
              <label className="flex items-center gap-2 pt-1">
                <Checkbox data-testid="acknowledge-shortfall" checked={acknowledgeShortfall} onCheckedChange={(v) => setAcknowledgeShortfall(!!v)} />
                I acknowledge the outstanding balance and approve Process handoff anyway
              </label>
            </div>
          )}
          {livePaymentType && (
            <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground" data-testid="live-payment-status-meta">
              <span>
                {paymentStatusLoading ? "Checking the current payment status…"
                  : paymentStatus?.as_of ? `Payment figures are live — as of ${fmtIST(paymentStatus.as_of)}`
                    : "Current payment status unavailable"}
              </span>
              <Button type="button" size="sm" variant="ghost" className="h-6 px-2 text-xs"
                data-testid="refresh-live-payment-status" disabled={paymentStatusLoading}
                onClick={() => refreshLivePaymentStatus(decide.approval)}>Refresh</Button>
            </div>
          )}
          {decide?.approve && decide?.approval?.type === "sa_premium_signup" && paymentStatus?.zero_payment && (
            <div className="rounded-md border border-red-500/40 bg-red-500/10 p-3 text-xs space-y-1" data-testid="premium-zero-payment-warning">
              <div className="flex items-center gap-1.5 font-medium text-red-600 dark:text-red-400">
                <AlertTriangle className="h-3.5 w-3.5" />No verified registration/booking payment
              </div>
              <p className="text-muted-foreground">The payment this request was raised on is no longer verified. Signup cannot be approved until a registration/booking payment is verified.</p>
            </div>
          )}
          {decide?.approve && decide?.approval?.type === "sa_premium_signup" && paymentStatus
            && !paymentStatus.has_shortfall && !paymentStatus.zero_payment && (
            <div className="rounded-md border p-3 text-xs space-y-1" data-testid="premium-payment-live-figures">
              <div className="font-medium">{paymentStatus.obligation_label || "Registration/Booking"} — fully paid</div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 font-mono tabular-nums">
                <span>Expected:</span><span>{inr(paymentStatus.expected)}</span>
                <span>Received (verified):</span><span>{inr(paymentStatus.paid)}</span>
                <span>Outstanding:</span><span>{inr(paymentStatus.outstanding)}</span>
              </div>
            </div>
          )}
          {decide?.approve && decide?.approval?.type === "sa_premium_signup" && paymentStatus?.has_shortfall && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs space-y-2" data-testid="premium-payment-shortfall-warning">
              <div className="flex items-center gap-1.5 font-medium text-amber-600 dark:text-amber-400">
                <AlertTriangle className="h-3.5 w-3.5" />REGISTRATION/BOOKING PAYMENT SHORTFALL
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 font-mono tabular-nums">
                <span>{paymentStatus.obligation_label || "Registration/Booking"}:</span><span>{inr(paymentStatus.expected)}</span>
                <span>Received (verified):</span><span>{inr(paymentStatus.paid)}</span>
                <span className="font-medium">Outstanding:</span><span className="font-medium">{inr(paymentStatus.outstanding)}</span>
              </div>
              <p>You may approve Signup while this balance remains outstanding (the time-based
                Second Instalment and the post-Admission Third/Final Instalment are never part
                of this figure).</p>
              <label className="flex items-center gap-2 pt-1">
                <Checkbox data-testid="acknowledge-premium-shortfall" checked={acknowledgeShortfall} onCheckedChange={(v) => setAcknowledgeShortfall(!!v)} />
                I acknowledge the outstanding balance and approve Signup anyway
              </label>
            </div>
          )}
          {decide?.approve && decide?.approval?.type === "product_change"
            && decide?.approval?.payload?.requires_financial_reconciliation_ack && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs space-y-2"
                data-testid="financial-reconciliation-warning">
              <div className="flex items-center gap-1.5 font-medium text-amber-600 dark:text-amber-400">
                <AlertTriangle className="h-3.5 w-3.5" />VERIFIED PAYMENT EXISTS
              </div>
              <p>A verified payment exists on this opportunity. The payment itself will not be
                altered — reconciling it for the new product is a separate step afterward.
                Approving this also requires the finance.config capability.</p>
              <label className="flex items-center gap-2 pt-1">
                <Checkbox data-testid="acknowledge-financial-reconciliation"
                  checked={acknowledgeFinancialReconciliation}
                  onCheckedChange={(v) => setAcknowledgeFinancialReconciliation(!!v)} />
                I acknowledge the verified payment and approve this product change anyway
              </label>
            </div>
          )}
          {decide?.approve && decide?.approval?.type === "payment_reallocation"
            && decide?.approval?.payload?.gst_mismatch && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs space-y-2"
                data-testid="gst-mismatch-warning">
              <div className="flex items-center gap-1.5 font-medium text-amber-600 dark:text-amber-400">
                <AlertTriangle className="h-3.5 w-3.5" />GST/REIMBURSEMENT TREATMENT DIFFERS
              </div>
              <p>Source rate: {decide.approval.payload.source_gst_rate} · Target rate: {decide.approval.payload.target_gst_rate}.
                Confirm the applicable tax treatment for this reallocation — never left to a silent default.</p>
              <label className="flex items-center gap-2 pt-1">
                <Checkbox data-testid="acknowledge-gst-mismatch" checked={acknowledgeGstMismatch}
                  onCheckedChange={(v) => setAcknowledgeGstMismatch(!!v)} />
                I confirm the tax treatment below and approve this reallocation anyway
              </label>
              <Textarea data-testid="confirmed-tax-treatment" placeholder="Describe the applicable tax treatment"
                value={confirmedTaxTreatment} onChange={(e) => setConfirmedTaxTreatment(e.target.value)} />
            </div>
          )}
          {decide?.approve && decide?.approval?.type === "task_completion"
            && decide?.approval?.requested_by === user?.user_id && (
            <div className="rounded-md border border-red-500/40 bg-red-500/10 p-3 text-xs space-y-2"
                data-testid="task-completion-self-approve-warning">
              <div className="flex items-center gap-1.5 font-medium text-red-600 dark:text-red-400">
                <AlertTriangle className="h-3.5 w-3.5" />YOU SUBMITTED THIS COMPLETION
              </div>
              <p>The approver must not be the same employee who submitted the completion.
                {user?.role === "admin"
                  ? " As admin, you may override this — the reason above is required and audited."
                  : " Only admin can override this restriction."}</p>
              {user?.role === "admin" && (
                <label className="flex items-center gap-2 pt-1">
                  <Checkbox data-testid="emergency-override" checked={emergencyOverride}
                    onCheckedChange={(v) => setEmergencyOverride(!!v)} />
                  I confirm this admin emergency override and approve my own submission anyway
                </label>
              )}
            </div>
          )}
          {decide?.approval?.type === "task_completion" && decide?.approval?.contact_id && (
            <Link className="text-xs underline decoration-dotted hover:text-primary"
              to={`/customers/${decide.approval.contact_id}`} data-testid="task-completion-view-customer">
              View Customer 360 →
            </Link>
          )}
          {decide?.approve && decide?.approval?.type === "signup_case" && correctionOptions.length > 1 && (
            <div className="space-y-1">
              <Label className="text-xs">Product (correct if the wrong one was picked)</Label>
              <Select value={productOverride} onValueChange={setProductOverride}>
                <SelectTrigger data-testid="decide-product-correction"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {correctionOptions.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {decide?.approve && ASSIGNEE_APPROVAL_TYPES.includes(decide?.approval?.type) && (
            <div className="space-y-1">
              <Label className="text-xs">Assign case to (optional)</Label>
              <Select value={assignee || "unassigned"} onValueChange={(v) => setAssignee(v === "unassigned" ? "" : v)}>
                <SelectTrigger data-testid="decide-assignee"><SelectValue placeholder="Unassigned — assign later from Process" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned — assign later from Process</SelectItem>
                  {/* The assignee becomes the Case's Process owner, so the
                      same server-side eligible list as every other owner picker. */}
                  {processEmployees.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <DialogFooter><Button data-testid="decision-submit" variant={decide?.approve ? "default" : "destructive"}
            disabled={!!(livePaymentType && paymentStatusLoading) || !!(
              decide?.approve && decide?.approval?.type === "sa_premium_signup"
              && paymentStatus?.zero_payment) || !!(
              decide?.approve && paymentStatus?.has_shortfall && !acknowledgeShortfall) || !!(
              decide?.approve && decide?.approval?.type === "product_change"
              && decide?.approval?.payload?.requires_financial_reconciliation_ack
              && !acknowledgeFinancialReconciliation) || !!(
              decide?.approve && decide?.approval?.type === "payment_reallocation"
              && decide?.approval?.payload?.gst_mismatch
              && (!acknowledgeGstMismatch || !confirmedTaxTreatment.trim())) || !!(
              decide?.approve && decide?.approval?.type === "task_completion"
              && decide?.approval?.requested_by === user?.user_id
              && !(user?.role === "admin" && emergencyOverride && note.trim())) || !!(
              !decide?.approve && decide?.approval?.type === "task_completion" && !note.trim())}
            onClick={submit}>Confirm {decide?.approve ? "approval" : (decide?.clarification ? "clarification request" : "rejection")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
